import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatCurrency } from "../../utils/formatters";
import creditService, { creditLineLabel } from "../../services/creditService.js";

// What the store owes on an order, as the invoice shows it: the order totals view's OrderTotal
// (products + unit fees + samples) less any refund and the client credits applied.
const orderTotalAfterCredits = (order) =>
  (order.orderTotal || 0) - (order.refundAmount || 0) - (order.creditAmount || 0);

// One line of the Order Summary breakdown; deductions show in red, in parentheses.
const SummaryLine = ({ label, amount, deduction = false, indent = false }) => (
  <div className="d-flex justify-content-between mb-1">
    <span className={indent ? "text-muted ps-3" : "text-muted"}>{label}</span>
    <span className={deduction ? "text-danger" : undefined}>
      {deduction ? `(${formatCurrency(amount)})` : formatCurrency(amount)}
    </span>
  </div>
);

// Status configurations for tabs
export const ORDER_STATUS_TABS = {
  pendingDelivery: {
    label: "Pending Delivery",
    filter: "OptDeliveryStatusID ne 2 and OptOrderStatusID ne 4",
    emptyMessage: "No pending deliveries",
    emptyIcon: "bi-clock"
  },
  delivered: {
    label: "Delivered",
    filter: "OptDeliveryStatusID eq 2",
    emptyMessage: "No delivered orders yet",
    emptyIcon: "bi-truck"
  },
  unpaid: {
    label: "Unpaid",
    filter: "OptPaymentStatusID ne 2 and OptOrderStatusID ne 4",
    emptyMessage: "No unpaid orders",
    emptyIcon: "bi-credit-card"
  },
  paid: {
    label: "Paid",
    filter: "OptPaymentStatusID eq 2",
    emptyMessage: "No paid orders yet",
    emptyIcon: "bi-check-circle"
  },
  all: {
    label: "All Orders",
    filter: "OptOrderStatusID ne 4",
    emptyMessage: "No orders found",
    emptyIcon: "bi-box-seam"
  }
};

// Status badge configurations
export const getDeliveryStatusInfo = (statusId) => {
  const statusMap = {
    1: { label: "Pending", variant: "warning", icon: "bi-clock" },
    2: { label: "Delivered", variant: "success", icon: "bi-check-circle" },
    3: { label: "In Transit", variant: "info", icon: "bi-truck" },
    4: { label: "Cancelled", variant: "danger", icon: "bi-x-circle" }
  };
  return statusMap[statusId] || { label: "Unknown", variant: "secondary", icon: "bi-question-circle" };
};

export const getFulfillmentStatusInfo = (statusId) => {
  const statusMap = {
    1: { label: "Processing", variant: "warning", icon: "bi-hourglass-split" },
    2: { label: "Fulfilled", variant: "success", icon: "bi-check2-all" },
    3: { label: "Partial", variant: "info", icon: "bi-pie-chart" },
    4: { label: "Cancelled", variant: "danger", icon: "bi-x-lg" }
  };
  return statusMap[statusId] || { label: "Unknown", variant: "secondary", icon: "bi-question-circle" };
};

export const getPaymentStatusInfo = (statusId) => {
  const statusMap = {
    1: { label: "Unpaid", variant: "danger", icon: "bi-credit-card" },
    2: { label: "Paid", variant: "success", icon: "bi-check-circle" },
    3: { label: "Due", variant: "warning", icon: "bi-x-circle" },
    4: { label: "Partial", variant: "info", icon: "bi-arrow-counterclockwise" }
  };
  return statusMap[statusId] || { label: "Unknown", variant: "secondary", icon: "bi-question-circle" };
};

// Order Row Component with expandable details
export const OrderRow = ({ order, isExpanded, onToggle, orderDetails, isLoadingDetails, showStore, onPrint, isPrinting }) => {
  const deliveryStatus = getDeliveryStatusInfo(order.optDeliveryStatusID);
  const fulfillmentStatus = getFulfillmentStatusInfo(order.optFulfillmentStatusID);
  const paymentStatus = getPaymentStatusInfo(order.optPaymentStatusID);

  const colSpan = showStore ? 9 : 8;

  // The credits applied to this order, one line each (same breakdown as the invoice). Fetched only
  // once the row is open and the order has credits; until then the summary shows the credit total.
  const hasCredits = order.creditAmount > 0;
  const { data: creditLines } = useQuery({
    queryKey: ["orderCredits", order.orderID],
    queryFn: () => creditService.getOrderCredits(order.orderID),
    enabled: isExpanded && hasCredits,
    staleTime: 2 * 60 * 1000,
  });

  return (
    <>
      <tr
        className="cursor-pointer"
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      >
        <td className="text-center">
          <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} text-muted`}></i>
        </td>
        <td>
          <span className="fw-semibold text-primary">
            #{order.legacyInvoice || order.orderID}
          </span>
        </td>
        {showStore && (
          <td>
            <span className="badge bg-light text-dark">
              {order.client?.name || 'Unknown Store'}
            </span>
          </td>
        )}
        <td>
          <span className="text-muted">
            {order.dateCreated
              ? new Date(order.dateCreated).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })
              : '—'}
          </span>
        </td>
        <td>
          {order.requestedDeliveryDate ? (
            <span>
              {new Date(order.requestedDeliveryDate).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
              })}
            </span>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td className="text-center">
          <span className="badge bg-light text-dark">
            {order.itemCount || 0} items
          </span>
        </td>
        <td className="text-end">
          <span className="fw-semibold">
            {formatCurrency(orderTotalAfterCredits(order))}
          </span>
        </td>
        <td className="text-center">
          <span className={`badge bg-${paymentStatus.variant}`}>
            <i className={`bi ${paymentStatus.icon} me-1`}></i>
            {paymentStatus.label}
          </span>
        </td>
        <td className="text-center">
          <span className={`badge bg-${deliveryStatus.variant}`}>
            <i className={`bi ${deliveryStatus.icon} me-1`}></i>
            {deliveryStatus.label}
          </span>
        </td>
      </tr>

      {/* Expanded Order Details */}
      {isExpanded && (
        <tr className="bg-light">
          <td colSpan={colSpan} className="p-0">
            <div className="p-4">
              {isLoadingDetails ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2 text-muted">Loading order details...</span>
                </div>
              ) : orderDetails ? (
                <div className="row">
                  {/* Order Items */}
                  <div className="col-lg-8">
                    <h6 className="mb-3">
                      <i className="bi bi-box me-2"></i>
                      Order Items
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="table-secondary">
                          <tr>
                            <th>Product</th>
                            <th className="text-center" style={{ width: "100px" }}>Qty</th>
                            <th className="text-end" style={{ width: "100px" }}>Price</th>
                            <th className="text-end" style={{ width: "100px" }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(orderDetails.orderItems || []).map((item) => (
                            <tr key={item.id}>
                              <td>
                                <div>
                                  <span className="fw-medium">
                                    {item.productSKUVariant?.name || 'Unknown Product'}
                                  </span>
                                  {item.productSKUVariant?.productSKU?.brand?.name && (
                                    <small className="text-muted d-block">
                                      {item.productSKUVariant.productSKU.brand.name}
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td className="text-center">{item.unitQuantity || 0}</td>
                              <td className="text-end">{formatCurrency(item.unitPrice || 0)}</td>
                              <td className="text-end fw-medium">
                                {formatCurrency((item.unitQuantity || 0) * (item.unitPrice || 0))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="col-lg-4">
                    <h6 className="mb-3">
                      <i className="bi bi-receipt me-2"></i>
                      Order Summary
                    </h6>
                    <div className="card bg-white">
                      <div className="card-body">
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Fulfillment</span>
                          <span className={`badge bg-${fulfillmentStatus.variant}`}>
                            {fulfillmentStatus.label}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Payment</span>
                          <span className={`badge bg-${paymentStatus.variant}`}>
                            {paymentStatus.label}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Delivery</span>
                          <span className={`badge bg-${deliveryStatus.variant}`}>
                            {deliveryStatus.label}
                          </span>
                        </div>
                        <hr />
                        <SummaryLine label="Subtotal" amount={order.itemSubtotal || 0} />
                        {order.sampleTotal > 0 && <SummaryLine label="Samples" amount={order.sampleTotal} />}
                        {order.itemUnitFee > 0 && <SummaryLine label="Unit Fee" amount={order.itemUnitFee} />}
                        {order.refundAmount > 0 && <SummaryLine label="Refund" amount={order.refundAmount} deduction />}
                        {hasCredits && (creditLines?.length > 0 ? (
                          <>
                            <div className="text-muted mb-1">Credits</div>
                            {creditLines.map((line, i) => (
                              <SummaryLine key={i} label={creditLineLabel(line)} amount={line.amount} deduction indent />
                            ))}
                          </>
                        ) : (
                          <SummaryLine label="Credits" amount={order.creditAmount} deduction />
                        ))}
                        <div className="d-flex justify-content-between mt-2">
                          <span className="fw-semibold">Order Total</span>
                          <span className="fw-bold text-primary fs-5">
                            {formatCurrency(orderTotalAfterCredits(order))}
                          </span>
                        </div>
                        {orderDetails.specialNotes && (
                          <>
                            <hr />
                            <div>
                              <small className="text-muted d-block mb-1">Special Notes:</small>
                              <small>{orderDetails.specialNotes}</small>
                            </div>
                          </>
                        )}
                        <hr />
                        <button
                          className="btn btn-outline-primary w-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPrint(order.orderID);
                          }}
                          disabled={isPrinting}
                        >
                          {isPrinting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Preparing...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-printer me-2"></i>
                              Print Order
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-3 text-muted">
                  Unable to load order details
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
